import moment from "moment";

const CACHE_KEY = "tcs_daily_hours_monthly_access_v1";
/** Soft TTL so opening the modal / revisiting Daily Hours does not always wait on access-status. */
const CACHE_TTL_MS = 60 * 1000;

export const CLEARED_MONTHLY_ACCESS_STATUS = {
  hasPendingUpdates: false,
  canAccessCurrentMonth: true,
  canEditPreviousMonth: false,
  requestStatus: null,
  hasCurrentMonthMissedUpdates: false,
  canEditCurrentMonthMissed: false,
  currentMonthRequestStatus: null,
  currentMonthMissedDates: [],
};

function readMonthlyAccessStatusCacheEntry() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.monthKey !== moment().format("YYYY-MM")) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readMonthlyAccessStatusCache() {
  const entry = readMonthlyAccessStatusCacheEntry();
  return entry?.data ?? null;
}

export function writeMonthlyAccessStatusCache(data) {
  if (!data) return;
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        monthKey: moment().format("YYYY-MM"),
        data,
        cachedAt: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

export function isMonthlyAccessWorkflowActive(status) {
  if (!status) return false;
  if (status.hasPendingUpdates) return true;
  if (status.canEditPreviousMonth) return true;
  if (status.hasCurrentMonthMissedUpdates) return true;
  if (status.canEditCurrentMonthMissed) return true;
  const requestStatus = String(status.requestStatus || "").toLowerCase();
  if (requestStatus === "pending" || requestStatus === "approved") return true;
  const currentStatus = String(status.currentMonthRequestStatus || "").toLowerCase();
  return currentStatus === "pending" || currentStatus === "approved";
}

/** HR/Admin review happens on another client, so a cached pending status is never trustworthy. */
export function hasPendingMonthlyAccessRequest(status) {
  if (!status) return false;
  return (
    String(status.requestStatus || "").toLowerCase() === "pending" ||
    String(status.currentMonthRequestStatus || "").toLowerCase() === "pending"
  );
}

export function shouldFetchMonthlyAccessStatus({ force = false, cached, maxAgeMs = CACHE_TTL_MS } = {}) {
  if (force) return true;
  const entry = readMonthlyAccessStatusCacheEntry();
  const resolvedCache =
    cached === undefined ? entry?.data ?? null : cached;
  if (!resolvedCache) return true;
  if (hasPendingMonthlyAccessRequest(resolvedCache)) return true;
  const cachedAt = entry?.cachedAt || 0;
  const ageMs = Date.now() - cachedAt;
  // Fresh cache is enough even while a missed-updates workflow is active.
  if (cachedAt > 0 && ageMs <= maxAgeMs) return false;
  if (isMonthlyAccessWorkflowActive(resolvedCache)) return true;
  return false;
}
